import { prisma } from "@/lib/db";
import { calculatePCorrect, scoreCandidate, MODE_CONFIG } from "@/lib/scheduler";
import type { CandidateState, ProblemStatus, SessionMode } from "@/lib/scheduler";
import { NextRequest, NextResponse } from "next/server";

export async function POST(request: NextRequest) {
  try {
    const body = await request.json();
    const {
      userId,
      sessionId,
      mode = "standard",
      recentWrong = [],      // problem IDs answered wrong recently
      recentShown = [],      // problem IDs shown recently (to avoid immediate repeats)
      consecutiveWrong = 0,  // how many wrong in a row
    } = body;

    if (!userId) {
      return NextResponse.json({ error: "userId required" }, { status: 400 });
    }

    const now = new Date();
    const config = MODE_CONFIG[mode as SessionMode];

    // 1. First priority: retry recently wrong items (after short delay)
    if (recentWrong.length > 0) {
      // Pick a random wrong item that wasn't just shown
      const retryPool = recentWrong.filter((id: string) => !recentShown.slice(-2).includes(id));
      if (retryPool.length > 0) {
        const retryId = retryPool[Math.floor(Math.random() * retryPool.length)];
        const problem = await prisma.problem.findUnique({ where: { id: retryId } });
        if (problem) {
          return NextResponse.json({
            problemId: problem.id,
            problemText: problem.problemText,
            groupId: problem.groupId,
            type: "retry",
          });
        }
      }
    }

    // 2. If struggling (2+ wrong in a row), pick an easy review item
    if (consecutiveWrong >= 2) {
      const easyReview = await prisma.userProblemState.findFirst({
        where: {
          userId,
          problemId: { notIn: recentShown.slice(-5) },
          consecutiveCorrect: { gte: 2 },
          memoryStrength: { gte: 0.5 },
        },
        include: { problem: true },
        orderBy: { memoryStrength: "desc" },
      });

      if (easyReview) {
        return NextResponse.json({
          problemId: easyReview.problemId,
          problemText: easyReview.problem.problemText,
          groupId: easyReview.problem.groupId,
          type: "review",
        });
      }
    }

    // 3. Due review items
    const dueItems = await prisma.userProblemState.findMany({
      where: {
        userId,
        dueAt: { lte: now },
        status: { notIn: ["new", "mastered"] },
        problemId: { notIn: recentShown.slice(-3) },
      },
      include: { problem: true },
      take: 20,
    });

    // 4. New items from active group (shuffled)
    const activeGroupState = await prisma.userGroupState.findFirst({
      where: { userId, status: "active" },
    });

    let newItems: { problem: { id: string; problemText: string; groupId: string } }[] = [];

    if (activeGroupState) {
      const unseenProblems = await prisma.problem.findMany({
        where: {
          groupId: activeGroupState.groupId,
          userProblemStates: { none: { userId } },
        },
        take: 10,
      });
      newItems = unseenProblems.map(p => ({ problem: p }));
    } else {
      // No active group - activate first one
      const firstGroup = await prisma.problemGroup.findFirst({
        orderBy: { sequence: "asc" },
      });
      if (firstGroup) {
        await prisma.userGroupState.create({
          data: {
            userId,
            groupId: firstGroup.id,
            status: "active",
            unlockedAt: now,
          },
        });
        const unseenProblems = await prisma.problem.findMany({
          where: {
            groupId: firstGroup.id,
            userProblemStates: { none: { userId } },
          },
          take: 10,
        });
        newItems = unseenProblems.map(p => ({ problem: p }));
      }
    }

    // 5. Score and select best candidate
    const candidates: Array<{
      problemId: string;
      problemText: string;
      groupId: string;
      type: "review" | "new";
      score: number;
    }> = [];

    // Score due items
    for (const item of dueItems) {
      const state: CandidateState = {
        status: item.status as ProblemStatus,
        memoryStrength: item.memoryStrength,
        difficulty: item.difficulty,
        consecutiveCorrect: item.consecutiveCorrect,
        consecutiveWrong: item.consecutiveWrong,
        lastResult: item.lastResult as "correct" | "wrong" | null,
        lastAnsweredAt: item.lastAnsweredAt,
        dueAt: item.dueAt,
        seenCountToday: item.seenCountToday,
        recentAvgResponseMs: item.recentAvgResponseMs,
        successCount: item.successCount,
      };
      const pCorrect = calculatePCorrect(state, now);
      const scored = scoreCandidate(
        item.problemId,
        item.problem.groupId,
        state,
        mode as SessionMode,
        now,
        null,
        "review"
      );
      candidates.push({
        problemId: item.problemId,
        problemText: item.problem.problemText,
        groupId: item.problem.groupId,
        type: "review",
        score: scored.finalScore + Math.random() * 0.1, // small randomness
      });
    }

    // Score new items (with shuffle via random score component)
    for (const item of newItems) {
      candidates.push({
        problemId: item.problem.id,
        problemText: item.problem.problemText,
        groupId: item.problem.groupId,
        type: "new",
        score: 0.3 + Math.random() * 0.2, // base score for new items with randomness
      });
    }

    if (candidates.length === 0) {
      return NextResponse.json({ done: true, message: "No more problems available" });
    }

    // Prefer review over new if we have due items, but mix in new occasionally
    const dueCount = candidates.filter(c => c.type === "review").length;
    const newCount = candidates.filter(c => c.type === "new").length;

    let selected;
    if (dueCount > 0 && Math.random() < 0.7) {
      // 70% chance to pick from due items
      const reviewCandidates = candidates.filter(c => c.type === "review");
      reviewCandidates.sort((a, b) => b.score - a.score);
      selected = reviewCandidates[0];
    } else if (newCount > 0) {
      // Pick randomly from new items
      const newCandidates = candidates.filter(c => c.type === "new");
      selected = newCandidates[Math.floor(Math.random() * newCandidates.length)];
    } else {
      // Fall back to highest scored
      candidates.sort((a, b) => b.score - a.score);
      selected = candidates[0];
    }

    return NextResponse.json({
      problemId: selected.problemId,
      problemText: selected.problemText,
      groupId: selected.groupId,
      type: selected.type,
    });
  } catch (error) {
    console.error("Error getting next problem:", error);
    return NextResponse.json(
      { error: error instanceof Error ? error.message : "Failed to get next problem" },
      { status: 500 }
    );
  }
}
